import React, { useState, useContext, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'motion/react';
import { Slide, toast } from 'react-toastify';
import strings from '../../../translations';
import { isMobile } from '../../../theme/theme';
import { ReactReduxContext } from 'react-redux';
import moment from 'moment';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPencilAlt,
  faPencilRuler,
  faBorderAll,
  faDownload,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';

import CircleButtonListItem from './CircleButtonListItem';
import DrawingToast from '../../toasts/DrawingToast';

import { ReactComponent as SvCircle } from '../../../theme/icons/drawtools_circle.svg';
import { ReactComponent as SvRectangle } from '../../../theme/icons/drawtools_rectangle.svg';
import { ReactComponent as SvPolygon } from '../../../theme/icons/drawtools_polygon.svg';
import { ReactComponent as SvLinestring } from '../../../theme/icons/drawtools_linestring.svg';
import { theme } from '../../../theme/theme';

import {
  pushGFILocations,
  resetGFILocations,
  setGFICroppingArea,
  setVKMData
} from '../../../state/slices/rpcSlice';

import {
  setMinimizeGfi,
  setSelectedDrawingTool,
  setHasToastBeenShown,
  setWarning,
  setActiveSelectionTool,
  setActiveTool,
  setMinimizeFeatureSelection,
  setIsGfiToolsOpen,
  setIsGfiOpen,
  addToGeoJsonArray
} from '../../../state/slices/uiSlice';

import SVLoader from '../../../utils/components/SvLoader';
import {
  DRAWING_TIP_LOCALSTORAGE,
  GFI_GEOMETRY_LAYER_ID,
  BODY_SIZE_EXCEED,
  GENERAL_FAIL,
  VECTOR_LAYER_ID,
  FEATURE_SELECTION_LAYER,
  FEATURE_SELECTION_DRAWING_TOOL
} from '../../../utils/constants';
import { useAppSelector } from '../../../state/hooks';

const StyledGfiToolContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 24px;
  overflow: auto;
  @media ${(props) => props.theme.device.mobileL} {
    padding: 16px;
  }
  background-color: white;
`;

const StyledToolsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  filter: ${(props) => (props.isGfiLoading ? 'blur(3px)' : 'none')};
`;

const StyledDrawingToolsContainer = styled(motion.div)`
  padding-left: 2px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StyledLoaderWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 999;
  height: 100%;
  max-width: 200px;
  max-height: 200px;
  transform: translate(-50%, -50%);
  svg {
    width: 100%;
    height: 100%;
    fill: none;
  }
`;

const StyledSavedView = styled.div`
  width: 100%;
  z-index: 1;
  min-height: 48px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  background-color: ${(props) => props.theme.colors.mainColor1};
  border-radius: 4px;
  padding: 8px 0px 8px 0px;
  box-shadow: 0px 3px 6px 0px rgba(0, 0, 0, 0.16);
  @-moz-document url-prefix() {
    position: initial;
  }
`;

const StyledSavedViewName = styled.p`
  user-select: none;
  max-width: 240px;
  color: ${(props) => props.theme.colors.mainWhite};
  margin: 0;
  padding: 0px;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.1s ease-in;
`;

const StyledSavedViewDescription = styled.div`
  margin: 0;
  padding: 0px;
  font-size: 12px;
  font-weight: 500;
  color: ${(props) => props.theme.colors.mainWhite};
`;

const StyledLeftContent = styled.div`
  display: flex;
  align-items: center;
`;

const StyledRightContent = styled.div`
  display: flex;
  align-items: center;
`;

const StyleSavedViewHeaderIcon = styled.div`
  width: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
  svg {
    font-size: 20px;
    color: ${(props) => props.theme.colors.mainWhite};
  }
  p {
    margin: 0;
    font-weight: bold;
    font-size: 22px;
    color: ${(props) => props.theme.colors.mainWhite};
  }
`;

const StyledSavedViewTitleContent = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;
const StyledToastIcon = styled(FontAwesomeIcon)`
  color: ${theme.colors.mainColor1};
`;

// Define default icon, if null then use cropping area name first char
const defaultIcon = null;

// Define here other than default icon (null = use cropping area name first char)
const icons = {
  0: {
    icon: faPencilRuler
  },
  3: {
    icon: faBorderAll
  },
  4: {
    icon: faBorderAll
  },
  5: {
    icon: faBorderAll
  }
};

const addFeaturesToMapParams = {
  layerId: GFI_GEOMETRY_LAYER_ID,
  featureStyle: {
    fill: {
      color: 'rgba(10, 140, 247, 0.1)'
    },
    stroke: {
      area: {
        color: 'rgba(100, 255, 95, 0.7)',
        width: 4,
        lineJoin: 'round'
      }
    },
    image: {
      shape: 5,
      size: 3,
      fill: {
        color: 'rgba(100, 255, 95, 0.7)'
      }
    }
  },
  clearPrevious: true
};

const FeatureDataSelectionToolsMenu = () => {
  const drawinToolsData = [
    {
      id: 'sv-measure-linestring',
      title: strings.tooltips.drawingTools.linestring,
      style: {
        icon: <SvLinestring />
      },
      type: 'LineString'
    },
    {
      id: 'sv-measure-polygon',
      title: strings.tooltips.drawingTools.polygon,
      style: {
        icon: <SvPolygon />
      },
      type: 'Polygon'
    },
    {
      id: 'sv-measure-box',
      title: strings.tooltips.drawingTools.box,
      style: {
        icon: <SvRectangle />
      },
      type: 'Box'
    },
    {
      id: 'sv-measure-circle',
      title: strings.tooltips.drawingTools.circle,
      style: {
        icon: <SvCircle />
      },
      type: 'Circle'
    }
  ];
  const { store } = useContext(ReactReduxContext);

  const { channel, selectedLayers, gfiLocations, selectedLayersByType } =
    useAppSelector((state) => state.rpc);

  const {
    gfiCroppingTypes,
    selectedDrawingTool,
    hasToastBeenShown,
    isGfiOpen,
    activeSelectionTool,
    activeTool
  } = useAppSelector((state) => state.ui);
  const [isGfiLoading, setIsGfiLoading] = useState(false);
  const [numberedLoader, setNumberedLoader] = useState(null);

  const [geometries, setGeometries] = useState([]);

  const [showToast, setShowToast] = useState(
    JSON.parse(localStorage.getItem(DRAWING_TIP_LOCALSTORAGE))
  );

  const handleClick = () => {
    setShowToast(false);
    toast.dismiss('measurementToast');
  };

  const handleGfiToolsMenu = () => {
    store.dispatch(setIsGfiToolsOpen(false));
    channel && activeTool === FEATURE_SELECTION_DRAWING_TOOL &&
      channel.postRequest("DrawTools.StopDrawingRequest", [
        FEATURE_SELECTION_DRAWING_TOOL,
        true,
      ]);

    setIsGfiToolsOpen &&
      channel &&
      channel.postRequest("VectorLayerRequest", [
        {
          layerId: FEATURE_SELECTION_LAYER,
          remove: true,
        },
      ]);
    store.dispatch(setIsGfiOpen(true));
  };

  const handleSelectTool = (id) => {
    if (activeSelectionTool !== id) {
      // set the selected tool as active
      store.dispatch(setActiveSelectionTool(id));

      if (id === 0 || id === 505) {
        channel.postRequest('MapModulePlugin.RemoveFeaturesFromMapRequest', [
          null,
          null,
          FEATURE_SELECTION_LAYER
        ]);
      } else {
        // minimize dialog
        store.dispatch(setMinimizeFeatureSelection(true));

        // start the spinner
        setIsGfiLoading(true);
        channel.getGfiCroppingArea(
          [id],
          function (data) {
            isGfiOpen && store.dispatch(setMinimizeGfi(true));
            setIsGfiLoading(false);

            let label = data.hasOwnProperty('labelProperty')
              ? data.labelProperty
              : null;

            let rn = 'MapModulePlugin.AddFeaturesToMapRequest';
            let options = {
              layerId: FEATURE_SELECTION_LAYER,
              clearPrevious: true,
              featureStyle: {
                fill: {
                  color: 'rgba(255, 255, 255, 0.5)'
                },
                stroke: {
                  color: '#0064af',
                  width: 2
                },
                text: {
                  fill: {
                    color: '#ffffff'
                  },
                  stroke: {
                    color: '#0064af',
                    width: 5
                  },
                  font: 'bold 12px Arial',
                  textAlign: 'center',
                  textBaseline: 'middle',
                  offsetX: 0,
                  offsetY: 0,
                  labelProperty: label,
                  overflow: id === 2 ? true : false
                }
              },
              hover: !isMobile
                ? {
                    featureStyle: {
                      fill: {
                        color: 'rgba(0, 99, 175, 0.7)'
                      },
                      stroke: {
                        color: '#0064af',
                        width: 2
                      },
                      text: {
                        fill: {
                          color: '#ffffff'
                        },
                        stroke: {
                          color: '#0064af',
                          width: 5
                        },
                        font: 'bold 16px Arial',
                        textAlign: 'center',
                        textBaseline: 'middle',
                        offsetX: 0,
                        offsetY: 0,
                        labelProperty: label,
                        overflow: id === 2 ? true : false
                      }
                    }
                  }
                : {}
            };
            data.geojson && channel.postRequest(rn, [data.geojson, options]);
          },
          function (err) {
            setIsGfiLoading(false);
          }
        );
      }
    } else {
      // Disable previously selected tool as we just clicked on it again
      // If we had selected a drawing tool, disable it
      if (selectedDrawingTool) {
        // stop and clear drawing
        channel.postRequest('DrawTools.StopDrawingRequest', [
            FEATURE_SELECTION_DRAWING_TOOL,
            true
          ]);
        store.dispatch(setSelectedDrawingTool(null));
        // dismiss measurement toast as drawing is not active anymore
        toast.dismiss('measurementToast');
      }
      store.dispatch(setActiveSelectionTool(null));
      channel.postRequest('MapModulePlugin.RemoveFeaturesFromMapRequest', [
        null,
        null,
        FEATURE_SELECTION_LAYER
      ]);
    }
  };

  const handleSelectDrawingTool = (id, item) => {
    if (id !== selectedDrawingTool) {
      // if we had selected a drawing tool previously, clear that
      if (selectedDrawingTool !== null) {
        channel.postRequest('DrawTools.StopDrawingRequest', [
          FEATURE_SELECTION_DRAWING_TOOL,
          true
        ]);
      }
      // minimize dialog
      store.dispatch(setMinimizeFeatureSelection(true));

      store.dispatch(setSelectedDrawingTool(id));
      var style = {
        draw: {
          fill: {
            color: 'rgba(255,255,255,0.5)'
          },
          stroke: {
            color: 'rgba(100, 255, 95, 0.7)',
            width: 3
          },
          image: {
            radius: 4,
            fill: {
              color: 'rgba(0,0,0,1)'
            }
          }
        },
        modify: {
          fill: {
            color: 'rgba(153,102,255,0.3)'
          },
          stroke: {
            color: 'rgba(0,0,0,1)',
            width: 2
          },
          image: {
            radius: 4,
            fill: {
              color: 'rgba(0,0,0,1)'
            }
          }
        },
        intersect: {
          fill: {
            color: 'rgba(255,255,255,0.3)'
          },
          stroke: {
            color: 'rgba(0,0,0,1)',
            width: 2,
            lineDash: 5
          },
          image: {
            radius: 4,
            fill: {
              color: 'rgba(0,0,0,1)'
            }
          }
        }
      };

      var data = [FEATURE_SELECTION_DRAWING_TOOL, item.type, { style: style }];
      channel.postRequest('DrawTools.StartDrawingRequest', data);
      store.dispatch(setActiveTool(FEATURE_SELECTION_DRAWING_TOOL));
      // do we want to minimize gfi when selecting features on map?
      isGfiOpen && store.dispatch(setMinimizeGfi(true));
      if (
        showToast !== false &&
        !hasToastBeenShown.includes('measurementToast')
      ) {
        if (item.type === 'LineString' || item.type === 'Polygon') {
          toast.info(
            <DrawingToast
              text={strings.tooltips.drawingTools.drawingToast}
              handleButtonClick={handleClick}
            />,
            {
              icon: <StyledToastIcon icon={faInfoCircle} />,
              toastId: 'measurementToast',
              onClose: () =>
                store.dispatch(
                  setHasToastBeenShown({
                    toastId: 'measurementsToast',
                    shown: true
                  })
                )
            }
          );
        }
      }
    } else {
      // we clicked on the same drawingtool so disable it and clear it
      channel.postRequest('DrawTools.StopDrawingRequest', [
          FEATURE_SELECTION_DRAWING_TOOL,
          true
        ]);
      // now we shouldn't have any drawingtool selected
      store.dispatch(setSelectedDrawingTool(null));
      // dismiss measurement toast as drawing is not active anymore
      toast.dismiss('measurementToast');
    }
  };

  const handleActivateSavedGeometry = async (features) => {
    // minimize dialog
    store.dispatch(setMinimizeFeatureSelection(true));

    channel.postRequest('MapModulePlugin.RemoveFeaturesFromMapRequest', [
      null,
      null,
      VECTOR_LAYER_ID
    ]);
    setIsGfiLoading(true);

    if (features.data[0].name === 'DrawingEvent') {
      store.dispatch(resetGFILocations([]));
      store.dispatch(setVKMData(null));
      channel.postRequest('MapModulePlugin.RemoveMarkersRequest', [
        'VKM_MARKER'
      ]);
      const fetchableLayers = selectedLayers.filter((layer) =>
        layer.groups?.every((group) => group !== 1)
      );
      const loaderLength =
        fetchableLayers.length * features.data[0].geojson.features.length;

      setNumberedLoader({ current: 0, total: loaderLength });

      store.dispatch(setGFICroppingArea(features.data[0].geojson.features));
      let index = 0;
      try {
        for (const layer of fetchableLayers) {
          if (layer.layerType === 'userlayer' || layer.layerType === 'wfs') {
            try {
              // Await the new wrapper for vector layers
              await fetchVectorFeaturesSynchronous(
                features.data[0].geojson.features[0],
                layer,
                features.data[0].geojson
              );
            } catch (err) {
              // errors handled inside wrapper
            }
            index++;
          } else {
            try {
              await fetchFeaturesSynchronous(
                features.data[0].geojson.features,
                layer,
                features.data[0].geojson
              );
            } catch (err) {
              // handled in fetchFeaturesSynchronous
            }
            index++;
          }
          if (fetchableLayers.length === index) {
            handleGfiToolsMenu();
            setIsGfiLoading(false);
          }
        }
      } catch (error) {
        // catch exception, when simplify geometry feature ready, catch BODY_SIZE_EXCEED
        // and make simplify and rerun query
        handleGfiToolsMenu();
        setIsGfiLoading(false);
      }
    } else if (features.data[0].data.geom) {
      store.dispatch(resetGFILocations([]));
      store.dispatch(setVKMData(null));
      channel.postRequest('MapModulePlugin.RemoveMarkersRequest', [
        'VKM_MARKER'
      ]);
      const fetchableLayers = selectedLayers.filter((layer) =>
        layer.groups?.every((group) => group !== 1)
      );
      const loaderLength =
        fetchableLayers.length * features.data[0].data.geom.features.length;

      setNumberedLoader({ current: 0, total: loaderLength });

      store.dispatch(setGFICroppingArea(features.data[0].data.geom.features));
      let index = 0;
      try {
        for (const layer of fetchableLayers) {
          if (layer.layerType === 'userlayer' || layer.layerType === 'wfs') {
            try {
              await fetchVectorFeaturesSynchronous(
                features.data[0].data.geom.features[0],
                layer,
                features.data[0].geojson
              );
            } catch (err) {
              // handled in wrapper
            }
            index++;
          } else {
            try {
              await fetchFeaturesSynchronous(
                features.data[0].data.geom.features,
                layer,
                features.data[0].geojson
              );
            } catch (err) {
              // handled in fetchFeaturesSynchronous
            }
            index++;
          }
          if (fetchableLayers.length === index) {
            handleGfiToolsMenu();
            setIsGfiLoading(false);
          }
        }
      } catch (error) {
        handleGfiToolsMenu();
        setIsGfiLoading(false);
      }
    }
  };

  const featureEventHandler = async (data) => {
    if (data.operation === 'click') {
      // Bring feature selection back after click
      store.dispatch(setMinimizeFeatureSelection(false));

      if (data.features) {
        store.dispatch(resetGFILocations([]));
        isGfiOpen && store.dispatch(setMinimizeGfi(false));
        setIsGfiLoading(true);
        const fetchableLayers = selectedLayers.filter((layer) =>
          layer.groups?.every((group) => group !== 1)
        );
        const loaderLength =
          fetchableLayers.length * data.features[0].geojson.features.length;

        setNumberedLoader({ current: 0, total: loaderLength });

        store.dispatch(setGFICroppingArea(data.features[0].geojson.features));
        let index = 0;
        try {
          for (const layer of fetchableLayers) {
            if (layer.layerType === 'userlayer' || layer.layerType === 'wfs') {
              try {
                await fetchVectorFeaturesSynchronous(
                  data.features[0].geojson.features[0],
                  layer,
                  data.features[0].geojson
                );
              } catch (err) {
                // handled in wrapper
              }
              index++;
            } else {
              try {
                await fetchFeaturesSynchronous(
                  data.features[0].geojson.features,
                  layer,
                  data.features[0].geojson
                );
              } catch (err) {
                // handled
              }
              index++;
            }
            if (fetchableLayers.length === index) {
              handleGfiToolsMenu();
              setIsGfiLoading(false);
            }
          }
        } catch (error) {
          //catch exception, when simplify geometry feature ready, catch BODY_SIZE_EXCEED
          //and make simplify and rerun query
          handleGfiToolsMenu();
          setIsGfiLoading(false);
        }
      }
    }
  };

  // returns empty object if error, otherwise return gfi location object
  const transformVectorFeaturesResponse = (data, geojson, layerId) => {
    if (!data || !data[layerId]) return {};

    const node = data[layerId] || {};
    const features = Array.isArray(node.features) ? node.features : [];
    const content = features.map((feat) => {
      const props = feat?.properties ? { ...feat.properties } : {};
      delete props.__fid;
      return { geojson: props };
    });

    return {
      layerId: layerId,
      gfiCroppingArea: geojson,
      type: 'json',
      content
    };
  };

  const fetchVectorFeaturesSynchronous = (featureArg, layer, geojson) => {
    return new Promise((resolve, reject) => {
      if (!channel || !channel.getVectorFeatures) {
        // No channel available: fail fast
        setNumberedLoader((prev) =>
          prev ? { current: prev.current + 1, total: prev.total } : prev
        );
        reject(GENERAL_FAIL);
        return;
      }

      const featureToSend = featureArg; 
      const layerId = [layer.id];

      channel.getVectorFeatures(
        [featureToSend, {'layers': layerId}],
        (vectorData) => {
          try {
            const transformed = transformVectorFeaturesResponse(vectorData, geojson, layer.id);

            if (Object.keys(transformed).length > 0) {
              store.dispatch(pushGFILocations(transformed));
            } else {
              toast.error(strings.gfi.errors.userlayerDataParseError, {
                position: 'top-center',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: 'colored',
                transition: Slide
              });
            }
            setNumberedLoader((prev) =>
              prev ? { current: prev.current + 1, total: prev.total } : prev
            );
            resolve('ok');
          } catch (e) {
            // transform error
            setNumberedLoader((prev) =>
              prev ? { current: prev.current + 1, total: prev.total } : prev
            );
            reject(GENERAL_FAIL);
          }
        },
        (error) => {
          setNumberedLoader((prev) =>
            prev ? { current: prev.current + 1, total: prev.total } : prev
          );

          if (error?.BODY_SIZE_EXCEEDED_ERROR) {
            toast.error(strings.gfi.errors.userLayerDataFetchError + layer.name, {
              position: 'top-center',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: 'colored',
              transition: Slide
            });
            handleGfiToolsMenu();
            setIsGfiLoading(false);
            reject(BODY_SIZE_EXCEED);
            return;
          }

          if (error === GENERAL_FAIL) {
            store.dispatch(
              setWarning({
                title: strings.generalError,
                subtitle: null,
                cancel: {
                  text: strings.general.ok,
                  action: () => {
                    setIsGfiLoading(false);
                    store.dispatch(setWarning(null));
                  }
                }
              })
            );
          }

          handleGfiToolsMenu();
          setIsGfiLoading(false);
          reject(GENERAL_FAIL);
        }
      );
    });
  };

  // Existing fetchFeaturesSynchronous kept as-is (slightly adapted to ensure consistent loader updates)
  const fetchFeaturesSynchronous = (feature, layer, geojson) => {
    return new Promise(function (resolve, reject) {
      channel.getFeaturesByGeoJSON(
        [feature, 0, [layer.id]],
        (gfiData) => {
          store.dispatch(setVKMData(null));
          channel.postRequest('MapModulePlugin.RemoveMarkersRequest', [
            'VKM_MARKER'
          ]);

          gfiData?.gfi?.forEach((gfi) => {
            if (gfi.content.length > 0) {
              const gfiLoc = {
                content: gfi.content,
                layerId: gfi.layerId,
                gfiCroppingArea: geojson,
                type: 'geojson',
                moreFeatures: gfi.content.some(
                  (content) => content.moreFeatures
                )
              };
              store.dispatch(pushGFILocations(gfiLoc));
            }
          });

          setNumberedLoader((prevState) => {
            return prevState
              ? { current: prevState.current + 1, total: prevState.total }
              : prevState;
          });
          resolve('ok');
        },
        function (error) {
          setNumberedLoader((prevState) => {
            return prevState
              ? { current: prevState.current + 1, total: prevState.total }
              : prevState;
          });
          if (error.BODY_SIZE_EXCEEDED_ERROR) {
            store.dispatch(
              setWarning({
                title: strings.bodySizeWarningTemporary,
                subtitle: null,
                cancel: {
                  text: strings.general.ok,
                  action: () => {
                    setIsGfiLoading(false);
                    store.dispatch(setWarning(null));
                  }
                }
              })
            );
            handleGfiToolsMenu();
            setIsGfiLoading(false);
            reject(BODY_SIZE_EXCEED);
            return;
          }
          if (error === GENERAL_FAIL) {
            store.dispatch(
              setWarning({
                title: strings.generalError,
                subtitle: null,
                cancel: {
                  text: strings.general.ok,
                  action: () => {
                    setIsGfiLoading(false);
                    store.dispatch(setWarning(null));
                  }
                }
              })
            );
          }
          handleGfiToolsMenu();
          setIsGfiLoading(false);
          reject(GENERAL_FAIL);
        }
      );
    });
  };

  useEffect(() => {
    let isSubscribed = true;
    channel &&
      channel.handleEvent('DrawingEvent', async (data) => {
        if (selectedDrawingTool) {

          if (isSubscribed && data.isFinished && data.isFinished === true) {
            // start spinner on feature selection only after we actually select the area
            setIsGfiLoading(true);

            // Bring feature selection back after click
            store.dispatch(setMinimizeFeatureSelection(false));

            activeTool === FEATURE_SELECTION_DRAWING_TOOL &&
              channel.postRequest('DrawTools.StopDrawingRequest', [
                FEATURE_SELECTION_DRAWING_TOOL,
                true
              ]);
            store.dispatch(setActiveTool(null));
            isGfiOpen && store.dispatch(setMinimizeGfi(false));
            store.dispatch(addToGeoJsonArray(data));
            store.dispatch(setSelectedDrawingTool(null));
            toast.dismiss('measurementToast');
            store.dispatch(resetGFILocations([]));

            const fetchableLayers = selectedLayers.filter(
              (layer) =>
                layer.groups?.every((group) => group !== 1) &&
                selectedLayersByType.backgroundMaps.filter(
                  (l) => l.id === layer.id
                ).length === 0
            );
            setNumberedLoader({ current: 0, total: fetchableLayers.length });

            store.dispatch(setGFICroppingArea(data.geojson.features));
            let index = 0;
            try {
              for (const layer of fetchableLayers) {
                if (layer.layerType === 'userlayer' || layer.layerType === 'wfs') {
                  try {
                    // Use the vector wrapper for user layers
                    await fetchVectorFeaturesSynchronous(
                      data.geojson.features[0],
                      layer,
                      data.geojson
                    );
                  } catch (err) {
                    // error handled in wrapper
                  }
                  index++;
                } else {
                  try {
                    await fetchFeaturesSynchronous(
                      data.geojson.features,
                      layer,
                      data.geojson
                    );
                  } catch (err) {
                    // handled inside fetchFeaturesSynchronous
                  }
                  index++;
                }

                if (fetchableLayers.length === index) {
                  handleGfiToolsMenu();
                  setIsGfiLoading(false);
                }
              }
            } catch (error) {
              // catch exception, when simplify geometry feature ready, catch BODY_SIZE_EXCEED
              // and make simplify and rerun query
              handleGfiToolsMenu();
              setIsGfiLoading(false);
            }
          }
        }
      });
    return () => {
      isSubscribed = false;
    };
  }, [activeTool, channel, selectedDrawingTool]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    store.dispatch(setActiveSelectionTool(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    gfiLocations.forEach((gfiLocation) => {
      gfiLocation.gfiCroppingArea &&
        channel.postRequest('MapModulePlugin.AddFeaturesToMapRequest', [
          gfiLocation.gfiCroppingArea,
          addFeaturesToMapParams
        ]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gfiLocations]);

  useEffect(() => {
    window.localStorage.getItem('geometries') !== null &&
      setGeometries(JSON.parse(window.localStorage.getItem('geometries')));

    channel && channel.handleEvent('FeatureEvent', featureEventHandler);
    return () => {
      channel &&
        channel.unregisterEventHandler('FeatureEvent', featureEventHandler);
      channel && setIsGfiLoading(false);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, channel]);

  return (
    <StyledGfiToolContainer id="gfiToolContainer" role="region" tabIndex="0">
      <AnimatePresence>
        {isGfiLoading && (
          <StyledLoaderWrapper
            id="loadingOverlay"
            transition={{
              duration: 0.2,
              type: 'tween'
            }}
            initial={{
              opacity: 0
            }}
            animate={{
              opacity: 1
            }}
            exit={{
              opacity: 0
            }}
          >
            <SVLoader />
            {numberedLoader && (
              <>
                {strings.gfi.loadingData} {numberedLoader.current} /{' '}
                {numberedLoader.total}{' '}
              </>
            )}
          </StyledLoaderWrapper>
        )}
      </AnimatePresence>

      <StyledToolsContainer isGfiLoading={isGfiLoading}>
        <CircleButtonListItem
          key={'cropping-type-draw'}
          id={0}
          icon={faPencilAlt}
          title={strings.gfi.draw}
          subtitle={strings.gfi.drawSubtitle}
          selectedItem={activeSelectionTool}
          handleSelectTool={handleSelectTool}
        />
        <AnimatePresence>
          {activeSelectionTool === 0 && (
            <StyledDrawingToolsContainer
              transition={{
                duration: 0.2,
                type: 'tween'
              }}
              initial={{
                opacity: 0,
                height: 0
              }}
              animate={{
                opacity: 1,
                height: 'auto'
              }}
              exit={{
                opacity: 0,
                height: 0
              }}
            >
              {drawinToolsData.map((tool) => {
                return (
                  <CircleButtonListItem
                    key={'cropping-type-' + tool.id}
                    id={tool.id}
                    item={tool}
                    title={tool.title}
                    subtitle={null}
                    selectedItem={selectedDrawingTool}
                    handleSelectTool={handleSelectDrawingTool}
                    size={'md'}
                    bgColor={'#ffffff'}
                    color={'#0064AF'}
                    activeColor={'#ffc107'}
                  >
                    {tool.style.icon}
                  </CircleButtonListItem>
                );
              })}
            </StyledDrawingToolsContainer>
          )}
        </AnimatePresence>

        <CircleButtonListItem
          key={'saved'}
          id={505}
          icon={faDownload}
          title={strings.gfi.savedGeometries.title}
          subtitle={strings.gfi.savedGeometries.subtitle}
          selectedItem={activeSelectionTool}
          handleSelectTool={handleSelectTool}
        />
        <AnimatePresence>
          {activeSelectionTool === 505 && (
            <StyledDrawingToolsContainer
              transition={{
                duration: 0.2,
                type: 'tween'
              }}
              initial={{
                opacity: 0,
                height: 0
              }}
              animate={{
                opacity: 1,
                height: 'auto'
              }}
              exit={{
                opacity: 0,
                height: 0
              }}
            >
              {geometries.map((geometry) => {
                return (
                  <StyledSavedView
                    key={geometry.name + (geometry.saveDate || '')}
                    onClick={(e) => {
                      e.preventDefault();
                      handleActivateSavedGeometry(geometry);
                    }}
                  >
                    <StyledLeftContent>
                      <StyleSavedViewHeaderIcon>
                        <p>{geometry.name.charAt(0).toUpperCase()}</p>
                      </StyleSavedViewHeaderIcon>
                      <StyledSavedViewTitleContent>
                        <StyledSavedViewName>
                          {geometry.name}
                        </StyledSavedViewName>
                        <StyledSavedViewDescription
                        >
                          {moment(geometry.saveDate).tz('Europe/Helsinki').format('DD.MM.YYYY HH:mm')}
                        </StyledSavedViewDescription>
                      </StyledSavedViewTitleContent>
                    </StyledLeftContent>
                    <StyledRightContent />
                  </StyledSavedView>
                );
              })}
            </StyledDrawingToolsContainer>
          )}
        </AnimatePresence>
        {gfiCroppingTypes &&
          gfiCroppingTypes.map((croppingType) => {
            return (
              <CircleButtonListItem
                key={'cropping-type-' + croppingType.id}
                id={croppingType.id}
                item={croppingType}
                icon={
                  icons[croppingType.id]
                    ? icons[croppingType.id].icon
                    : defaultIcon
                }
                title={croppingType.title}
                subtitle={croppingType.description}
                selectedItem={activeSelectionTool}
                handleSelectTool={handleSelectTool}
              />
            );
          })}
      </StyledToolsContainer>
    </StyledGfiToolContainer>
  );
};

export default FeatureDataSelectionToolsMenu;
